import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

/**
 * Privacy Policy — written to satisfy India's DPDP Act 2023 and the EU GDPR.
 * Public route (no auth required).
 */
const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading font-semibold text-xl text-white mb-3">{title}</h2>
    <div className="text-grey-300 font-body leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-void">
      <header className="border-b border-white/5 bg-void-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-grey-400 hover:text-white" aria-label="Go back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ShieldCheck className="w-5 h-5 text-neon-cyan" />
          <span className="font-heading font-bold text-white">Privacy Policy</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-grey-500 text-sm mb-8">Last updated: 31 May 2026</p>

        <p className="text-grey-300 font-body leading-relaxed mb-8">
          RevealIQ Meet ("we", "us", "our") respects your privacy. This policy explains what personal
          data we collect, why, and the rights you have under India's Digital Personal Data Protection
          Act, 2023 (DPDP) and the EU General Data Protection Regulation (GDPR).
        </p>

        <Section title="1. Data We Collect">
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Account data:</strong> name, email, and profile photo (via Google or email sign-up).</li>
            <li><strong>Meeting data:</strong> meeting titles, descriptions, schedules and participant lists.</li>
            <li><strong>Audio &amp; transcripts:</strong> when transcription is enabled, audio is processed to generate live transcripts and AI summaries.</li>
            <li><strong>Technical data:</strong> device/browser information and logs needed to run the service.</li>
          </ul>
        </Section>

        <Section title="2. Why We Process It (Lawful Basis)">
          <p>We process your data to provide the meeting service, on the basis of your <strong>consent</strong>
          and to perform our contract with you. You may withdraw consent at any time (see Section 6).</p>
        </Section>

        <Section title="3. AI Processing">
          <p>Transcription and AI features are powered by Kautilya AI and speech-to-text providers.
          Audio/transcript content is sent to these processors only to generate transcripts, summaries
          and assistant responses for your meeting. We do not sell your data.</p>
        </Section>

        <Section title="4. Data Retention">
          <p>Meeting metadata and AI notes are retained while your account is active. You can delete
          individual meetings at any time, and you can delete your entire account and associated data
          from <strong>Settings → Privacy &amp; Data</strong>.</p>
        </Section>

        <Section title="5. Data Sharing &amp; Transfers">
          <p>We share data only with sub-processors required to operate the service (authentication,
          real-time media, transcription and AI). These providers may process data outside your country;
          we take reasonable steps to ensure adequate protection.</p>
        </Section>

        <Section title="6. Your Rights">
          <p>Under DPDP and GDPR you have the right to access, correct, and erase your data, to withdraw
          consent, to data portability, and to grievance redressal. To exercise these:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Use <strong>Settings → Privacy &amp; Data</strong> to export or delete your data, or</li>
            <li>Email our Data Protection / Grievance Officer at <a className="text-neon-cyan hover:underline" href="mailto:privacy@revealiq.in">privacy@revealiq.in</a>.</li>
          </ul>
        </Section>

        <Section title="7. Cookies">
          <p>We use essential cookies/local storage for authentication and to remember your consent
          preferences. We do not use third-party advertising cookies.</p>
        </Section>

        <Section title="8. Children">
          <p>For school/class use, accounts for minors must be created and managed by a school or guardian
          who provides verifiable consent as required by applicable law.</p>
        </Section>

        <Section title="9. Contact">
          <p>Grievance Officer — RevealIQ Meet. Email:
          <a className="text-neon-cyan hover:underline ml-1" href="mailto:privacy@revealiq.in">privacy@revealiq.in</a></p>
        </Section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
